/**
 * Properties Panel
 * Edit properties for the currently selected layer
 * For image layers: info, fit settings, CSS filters, and SVG color overrides
 */

import React, { useMemo, useCallback } from 'react';
import {
  RotateCcw,
  Image as ImageIcon,
  FileType,
  Palette,
} from 'lucide-react';
import { useTemplateStore } from '@/stores';
import type { ImageLayer, Layer } from '@/types/layers';
import {
  parseFilterString,
  buildFilterString,
  type FilterValues,
} from '@/lib/svg/parser';

export function PropertiesPanel() {
  const { currentTemplate, selectedLayerIds, updateLayer } = useTemplateStore();

  // Get selected layer
  const selectedLayer = useMemo(() => {
    if (selectedLayerIds.length !== 1 || !currentTemplate) return null;

    // Search recursively for the layer
    const findLayer = (layers: Layer[]): Layer | null => {
      for (const layer of layers) {
        if (layer.id === selectedLayerIds[0]) return layer;
        if (layer.type === 'group') {
          const found = findLayer(layer.children);
          if (found) return found;
        }
      }
      return null;
    };

    return findLayer(currentTemplate.layers);
  }, [selectedLayerIds, currentTemplate]);

  if (!selectedLayer) {
    return (
      <div className="text-center py-8 text-fg-subtle">
        <p className="text-sm">Select a layer to edit its properties</p>
      </div>
    );
  }

  // Render different property panels based on layer type
  switch (selectedLayer.type) {
    case 'image':
      return <ImageProperties layer={selectedLayer} updateLayer={updateLayer} />;
    default:
      return (
        <div className="text-center py-8 text-fg-subtle">
          <p className="text-sm">
            Properties for {selectedLayer.type} layers coming soon
          </p>
        </div>
      );
  }
}

// Image layer properties
interface ImagePropertiesProps {
  layer: ImageLayer;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
}

function ImageProperties({ layer, updateLayer }: ImagePropertiesProps) {
  const { content } = layer;

  // Parse current filter values
  const filterValues = useMemo(() => {
    return parseFilterString(content.filter);
  }, [content.filter]);

  // Get file type from src
  const fileType = useMemo(() => {
    if (content.isSvg) return 'SVG';
    const src = content.src.toLowerCase();
    if (src.includes('data:image/png')) return 'PNG';
    if (src.includes('data:image/jpeg') || src.includes('data:image/jpg')) return 'JPG';
    if (src.includes('data:image/gif')) return 'GIF';
    if (src.includes('data:image/webp')) return 'WEBP';
    if (src.endsWith('.png')) return 'PNG';
    if (src.endsWith('.jpg') || src.endsWith('.jpeg')) return 'JPG';
    if (src.endsWith('.gif')) return 'GIF';
    if (src.endsWith('.webp')) return 'WEBP';
    if (src.endsWith('.svg')) return 'SVG';
    return 'Image';
  }, [content.src, content.isSvg]);

  // Update a single filter value
  const updateFilter = useCallback(
    (key: keyof FilterValues, value: number) => {
      const newFilters = { ...filterValues, [key]: value };
      const filterString = buildFilterString(newFilters);
      updateLayer(layer.id, {
        content: {
          ...content,
          filter: filterString || undefined,
        },
      } as Partial<ImageLayer>);
    },
    [layer.id, content, filterValues, updateLayer]
  );

  // Reset all filters
  const resetFilters = useCallback(() => {
    updateLayer(layer.id, {
      content: {
        ...content,
        filter: undefined,
      },
    } as Partial<ImageLayer>);
  }, [layer.id, content, updateLayer]);

  // Update object fit
  const updateObjectFit = useCallback(
    (value: 'cover' | 'contain' | 'fill' | 'none') => {
      updateLayer(layer.id, {
        content: {
          ...content,
          objectFit: value,
        },
      } as Partial<ImageLayer>);
    },
    [layer.id, content, updateLayer]
  );

  // Update border radius
  const updateBorderRadius = useCallback(
    (value: number) => {
      updateLayer(layer.id, {
        content: {
          ...content,
          borderRadius: value,
        },
      } as Partial<ImageLayer>);
    },
    [layer.id, content, updateLayer]
  );

  // Update color override
  const updateColorOverride = useCallback(
    (originalColor: string, newColor: string) => {
      const overrides = { ...(content.colorOverrides || {}) };
      if (newColor === originalColor) {
        delete overrides[originalColor];
      } else {
        overrides[originalColor] = newColor;
      }
      updateLayer(layer.id, {
        content: {
          ...content,
          colorOverrides: overrides,
        },
      } as Partial<ImageLayer>);
    },
    [layer.id, content, updateLayer]
  );

  // Reset all color overrides
  const resetColors = useCallback(() => {
    updateLayer(layer.id, {
      content: {
        ...content,
        colorOverrides: {},
      },
    } as Partial<ImageLayer>);
  }, [layer.id, content, updateLayer]);

  // Check if any filters are applied
  const hasFilters =
    filterValues.brightness !== 100 ||
    filterValues.contrast !== 100 ||
    filterValues.saturate !== 100 ||
    filterValues.hueRotate !== 0 ||
    filterValues.blur !== 0;

  // Check if any colors are overridden
  const hasColorOverrides =
    content.colorOverrides && Object.keys(content.colorOverrides).length > 0;

  return (
    <div className="space-y-6">
      {/* Info Section */}
      <section>
        <h4 className="label flex items-center gap-2">
          <ImageIcon size={12} />
          Image
        </h4>
        <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
          {/* Preview thumbnail */}
          <div className="w-16 h-16 flex-shrink-0 bg-navy rounded overflow-hidden flex items-center justify-center">
            <img
              src={content.src}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          </div>
          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-fg truncate" title={layer.name}>
              {layer.name}
            </p>
            <p className="text-xs text-fg-muted mt-1">
              {typeof layer.width === 'number' ? layer.width : '?'} ×{' '}
              {typeof layer.height === 'number' ? layer.height : '?'}
            </p>
            <span
              className={`
                inline-block mt-1 px-1.5 py-0.5 text-[10px] rounded
                ${content.isSvg
                  ? 'bg-purple-500/20 text-purple-300'
                  : 'bg-blue-500/20 text-blue-300'
                }
              `}
            >
              {fileType}
            </span>
          </div>
        </div>
      </section>

      {/* Fit Section */}
      <section>
        <h4 className="label flex items-center gap-2">
          <FileType size={12} />
          Fit
        </h4>
        <div className="space-y-3">
          {/* Object Fit */}
          <div>
            <label className="text-xs text-fg-muted block mb-1">Object Fit</label>
            <select
              value={content.objectFit || 'cover'}
              onChange={(e) =>
                updateObjectFit(e.target.value as 'cover' | 'contain' | 'fill' | 'none')
              }
              className="w-full bg-navy border border-white/10 rounded-lg px-3 py-2 text-sm text-fg focus:outline-none focus:border-primary/50"
            >
              <option value="cover">Cover</option>
              <option value="contain">Contain</option>
              <option value="fill">Fill</option>
              <option value="none">None</option>
            </select>
          </div>

          {/* Border Radius */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-fg-muted">Border Radius</label>
              <span className="text-xs text-fg-subtle">{content.borderRadius || 0}px</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={content.borderRadius || 0}
              onChange={(e) => updateBorderRadius(parseInt(e.target.value, 10))}
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h4 className="label flex items-center gap-2 mb-0">
            <Palette size={12} />
            Filters
          </h4>
          {hasFilters && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-1 text-xs text-fg-subtle hover:text-fg"
              title="Reset all filters"
            >
              <RotateCcw size={10} />
              Reset
            </button>
          )}
        </div>
        <div className="space-y-3">
          {/* Brightness */}
          <FilterSlider
            label="Brightness"
            value={filterValues.brightness ?? 100}
            onChange={(v) => updateFilter('brightness', v)}
            min={0}
            max={200}
            defaultValue={100}
            unit="%"
          />

          {/* Contrast */}
          <FilterSlider
            label="Contrast"
            value={filterValues.contrast ?? 100}
            onChange={(v) => updateFilter('contrast', v)}
            min={0}
            max={200}
            defaultValue={100}
            unit="%"
          />

          {/* Saturation */}
          <FilterSlider
            label="Saturation"
            value={filterValues.saturate ?? 100}
            onChange={(v) => updateFilter('saturate', v)}
            min={0}
            max={200}
            defaultValue={100}
            unit="%"
          />

          {/* Hue Rotate */}
          <FilterSlider
            label="Hue"
            value={filterValues.hueRotate ?? 0}
            onChange={(v) => updateFilter('hueRotate', v)}
            min={-180}
            max={180}
            defaultValue={0}
            unit="°"
          />

          {/* Blur */}
          <FilterSlider
            label="Blur"
            value={filterValues.blur ?? 0}
            onChange={(v) => updateFilter('blur', v)}
            min={0}
            max={20}
            defaultValue={0}
            unit="px"
          />
        </div>
      </section>

      {/* Colors Section (SVG only) */}
      {content.isSvg && content.detectedColors && content.detectedColors.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h4 className="label flex items-center gap-2 mb-0">
              <Palette size={12} />
              Colors
            </h4>
            {hasColorOverrides && (
              <button
                onClick={resetColors}
                className="flex items-center gap-1 text-xs text-fg-subtle hover:text-fg"
                title="Reset all colors"
              >
                <RotateCcw size={10} />
                Reset
              </button>
            )}
          </div>
          <div className="space-y-2">
            {content.detectedColors.map((color) => {
              const overriddenColor = content.colorOverrides?.[color] || color;
              const isOverridden = content.colorOverrides?.[color] !== undefined;

              return (
                <div
                  key={color}
                  className={`
                    flex items-center gap-3 p-2 rounded-lg
                    ${isOverridden ? 'bg-primary/10' : 'bg-white/5'}
                  `}
                >
                  {/* Original color swatch */}
                  <div
                    className="w-6 h-6 rounded border border-white/20 flex-shrink-0"
                    style={{ backgroundColor: color }}
                    title={`Original: ${color}`}
                  />

                  {/* Arrow */}
                  <span className="text-fg-subtle text-xs">→</span>

                  {/* Color picker */}
                  <div className="relative flex-1">
                    <input
                      type="color"
                      value={overriddenColor}
                      onChange={(e) => updateColorOverride(color, e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                    <div
                      className="w-6 h-6 rounded border border-white/20 cursor-pointer"
                      style={{ backgroundColor: overriddenColor }}
                    />
                  </div>

                  {/* Color value */}
                  <input
                    type="text"
                    value={overriddenColor.toUpperCase()}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                        updateColorOverride(color, val.toLowerCase());
                      }
                    }}
                    className="w-20 bg-navy border border-white/10 rounded px-2 py-1 text-xs text-fg font-mono focus:outline-none focus:border-primary/50"
                  />

                  {/* Reset button */}
                  {isOverridden && (
                    <button
                      onClick={() => updateColorOverride(color, color)}
                      className="p-1 text-fg-subtle hover:text-fg"
                      title="Reset to original"
                    >
                      <RotateCcw size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// Filter slider component
interface FilterSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  defaultValue: number;
  unit: string;
}

function FilterSlider({
  label,
  value,
  onChange,
  min,
  max,
  defaultValue,
  unit,
}: FilterSliderProps) {
  const isModified = value !== defaultValue;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs text-fg-muted">{label}</label>
        <span
          className={`text-xs ${isModified ? 'text-primary' : 'text-fg-subtle'}`}
        >
          {Math.round(value)}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full"
      />
    </div>
  );
}
